import { ApiError, CacheItem } from './types'

/**
 * Helper function to determine if an error is a cancellation
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isCancel = (error: any): boolean => {
  return error && error.name === 'AbortError'
}

/**
 * Determine if two parameter objects match: returns true if target is a subset of source
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isParamsMatch = (source?: any, target?: any): boolean => {
  if (!target || Object.keys(target).length === 0) return true
  if (!source) return false
  return Object.entries(target).every(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      return isParamsMatch(source[key], value)
    }
    return source[key] === value
  })
}

/**
 * Calculate URL based on template and urlParams, replacing placeholders with actual parameters
 * Also combines baseURL with the path if provided
 */
export const computeUrl = (
  template: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  urlParams?: any,
  baseURL?: string
): string => {
  // Replace parameters in URL template
  const urlWithParams = template.replace(/{(\w+)}/g, (_, key) =>
    urlParams && urlParams[key] !== undefined
      ? String(urlParams[key])
      : `{${key}}`
  )

  // If no baseURL is provided, return the processed URL directly
  if (!baseURL) {
    return urlWithParams
  }

  // If the template is already an absolute URL (starting with http or https), return it directly
  if (/^(https?:)?\/\//.test(urlWithParams)) {
    return urlWithParams
  }

  // Ensure baseURL ends with / and urlWithParams doesn't start with /
  const normalizedBase = baseURL.endsWith('/') ? baseURL : `${baseURL}/`
  const normalizedPath = urlWithParams.startsWith('/')
    ? urlWithParams.substring(1)
    : urlWithParams

  // Combine baseURL and path
  return `${normalizedBase}${normalizedPath}`
}

/**
 * Check if URL contains unresolved placeholders, if so return a unified error object, otherwise return null
 */
export function checkUnresolved(computedUrl: string) {
  if (/{(\w+)}/.test(computedUrl)) {
    console.error('URL contains unresolved parameters:', computedUrl)
    return Promise.reject(new Error('URL contains unresolved parameters'))
  }
  return null
}

/**
 * Stable object serialization function: sorts objects to ensure consistent string generation for identical content
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stableStringify(obj: any): string {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj)
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map(stableStringify).join(',') + ']'
  }
  const keys = Object.keys(obj).sort()
  return (
    '{' +
    keys
      .map((key) => JSON.stringify(key) + ':' + stableStringify(obj[key]))
      .join(',') +
    '}'
  )
}

/**
 * Generate a unique request key based on URL and parameters
 */
export function getRequestKey(url: string, params: unknown): string {
  return `${url}-${stableStringify(params)}`
}

/**
 * Check if cache is still valid
 */
export function isValidCache<T>(cache: CacheItem<T>): boolean {
  if (!cache.ttl) return true
  return Date.now() - cache.timestamp < cache.ttl
}

/**
 * Retry function with specified attempts and delay
 */
export async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  times: number,
  delay: number
): Promise<T> {
  try {
    return await fetchFn()
  } catch (err) {
    if (times === 0 || isCancel(err)) throw err
    await new Promise((resolve) => setTimeout(resolve, delay))
    return fetchWithRetry(fetchFn, times - 1, delay)
  }
}

/**
 * Create a debounced or throttled function
 */
export function createThrottledFetch<T>(
  fetch: () => Promise<T>,
  options: {
    type: 'debounce' | 'throttle'
    wait: number
  }
): () => Promise<T> {
  if (options.type === 'debounce') {
    let timer: NodeJS.Timeout | null = null
    let resolver: ((value: T | PromiseLike<T>) => void) | null = null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rejecter: ((reason?: any) => void) | null = null

    return () => {
      return new Promise<T>((resolve, reject) => {
        resolver = resolve
        rejecter = reject

        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          fetch().then(resolver!).catch(rejecter!)
        }, options.wait)
      })
    }
  } else {
    // Throttle implementation
    let lastCall = 0
    let pendingPromise: Promise<T> | null = null

    return () => {
      const now = Date.now()
      if (now - lastCall < options.wait) {
        return pendingPromise!
      }

      lastCall = now
      pendingPromise = fetch()
      return pendingPromise
    }
  }
}

/**
 * Extract error details from any error type
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractErrorDetails(err: any): ApiError {
  const apiError: ApiError = {
    message: String(err),
    rawError: err
  }

  if (err && typeof err === 'object' && err.response) {
    apiError.statusCode = err.response.status
    apiError.isNetworkError = !err.response
  }

  return apiError
}
